import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { HcPopComponent } from '@healthcatalyst/cashmere';
import { AvailableDataColumn } from '../../models/grid';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { readFromStorage, writeToStorage } from '../../utils/local-storage.utils';

interface CachedColumn {
    key: string;
    isHidden: boolean;
}
@Component({
    selector: 'cdp-grid-col-menu',
    templateUrl: './grid-col-menu.component.html',
    styleUrls: ['./grid-col-menu.component.scss'],
    standalone: false
})
export class GridColMenuComponent<T> implements OnInit {
    @ViewChild('columns') menu!: HcPopComponent;
    @Output() displayedColumnChange: EventEmitter<string[]> = new EventEmitter<string[]>();

    @Input() gridSettingsStorageKey = '';
    @Input() set staticPrefixCols(prefixCols: string[]) {
        this._staticPrefixCols = prefixCols;
        this.refreshCols();
    }
    get staticPrefixCols(): string[] {
        return this._staticPrefixCols;
    }
    private _staticPrefixCols: string[] = [];

    @Input() set staticSuffixCols(suffixCols: string[]) {
        this._staticSuffixCols = suffixCols;
        this.refreshCols();
    }
    get staticSuffixCols(): string[] {
        return this._staticSuffixCols;
    }
    private _staticSuffixCols: string[] = [];

    @Input() set availableDataCols(cols: Array<AvailableDataColumn<T>>) {
        this._availableDataCols = cols;
        this.generateColumnSelectionForms(cols);
        this.getCachedPreferences();
    }
    get availableDataCols(): Array<AvailableDataColumn<T>> {
        return this._availableDataCols;
    }
    private _availableDataCols = new Array<AvailableDataColumn<T>>();

    displayedColumns: string[];
    colSelectionForm = new FormGroup({});

    ngOnInit(): void {
        this.generateColumnSelectionForms(this._availableDataCols);
        this.getCachedPreferences();
    }

    get allHidden(): boolean {
        return !Object.values(this.colSelectionForm.controls).some(c => (c as FormControl).value);
    }

    selectAll() {
        Object.values(this.colSelectionForm.controls).forEach(c => (c as FormControl).setValue(true));
        this.onColChange();
    }

    clearAll() {
        Object.values(this.colSelectionForm.controls).forEach(c => (c as FormControl).setValue(false));
        this.onColChange();
    }

    onColChange() {
        this.refreshCols();
        this.saveCachedPreferences();
    }

    open() {
        this.menu.open();
    }

    generateColumnSelectionForms(cols: Array<AvailableDataColumn<T>>) {
        this.colSelectionForm = new FormGroup({});
        cols.filter(c => c.isHidable).forEach(c => {
            this.colSelectionForm.addControl(c.key, new FormControl(c.isShownByDefault));
        });
        this.refreshCols();
    }

    refreshCols() {
        // merge user controlled columns with static columns
        this.displayedColumns = this.staticPrefixCols.concat(this.getDisplayedColumns()).concat(this.staticSuffixCols);
        setTimeout(() => this.displayedColumnChange.emit(this.displayedColumns));
        return this.displayedColumns;
    }

    getDisplayedColumns(): string[] {
        return this.availableDataCols
            .filter(col => (col.isHidable ? this.colSelectionForm.get(col.key)?.value : true))
            .map(cd => cd.name as string);
    }

    /**
     * Handle rearanging of columns via drag and drop
     * @param event contains data from the drag and drop event
     * @param isInDataGrid if true, we need to take in account the fact that some columns may currently be hidden
     */
    columnDropped(event: CdkDragDrop<any>, isInDataGrid = true) {
        if (event) {
            const prevIndex = isInDataGrid
                ? this._availableDataCols.findIndex(c => c.key === event.item.data.key)
                : event.previousIndex;
            const adjustedIndexForPrefixes = event.currentIndex + this.staticPrefixCols.length;
            const newIndex = isInDataGrid
                ? this._availableDataCols.findIndex(c => c.key === this.displayedColumns[adjustedIndexForPrefixes])
                : event.currentIndex;
            moveItemInArray(this._availableDataCols, prevIndex, newIndex);
            this.refreshCols();
            this.saveCachedPreferences();
        }
    }

    /**
     * save ordered list of columns with their display status
     */
    saveCachedPreferences() {
        if (!this.gridSettingsStorageKey) {
            return;
        } // don't save if no key is provided
        const cachedColumns: CachedColumn[] = this.availableDataCols.map(col => ({
            key: col.key,
            isHidden: !this.colSelectionForm.get(col.key)?.value
        }));
        writeToStorage(this.gridSettingsStorageKey, cachedColumns);
    }

    /**
     * load user preferences for columns order and display status
     */
    getCachedPreferences() {
        if (!this.gridSettingsStorageKey) {
            return;
        } // don't load if no key is provided
        const cachedColumns: CachedColumn[] = readFromStorage(this.gridSettingsStorageKey) || new Array<CachedColumn>();
        const orderedAvailableColumns = new Array<AvailableDataColumn<T>>();
        cachedColumns?.forEach(col => {
            const existingColumn = this._availableDataCols.find(c => c.key === col.key);
            if (!existingColumn) {
                return;
            } // watch for columns that don't exist anymore

            // if the cached column still exists, set display status & add at correct index
            this.colSelectionForm.get(col.key)?.setValue(!col.isHidden);
            orderedAvailableColumns.push(existingColumn);
        });

        // if available columns don't exist in cache (like a newly introduced column),
        // add them in at their default index
        this._availableDataCols.forEach((availableColumn, index) => {
            if (!cachedColumns.find(c => c.key === availableColumn.key)) {
                orderedAvailableColumns.splice(index, 0, availableColumn);
            }
        });

        this._availableDataCols = orderedAvailableColumns;
        this.refreshCols();
    }
}
